import styled from '@2pulse/responsive';
import { normalize } from '@app/helpers';
import { col } from '@app/style';
import { withComponents } from '@app/helpers';
import { theme } from '@app/theme';
import { Button } from '../form/buttons';
import { triptychSelectors } from '@app/store';
import { useAppSelector } from '@app/store/hooks';
import { AppPath } from '@tickeat/types';
import { useCheckPermissions } from '@app/hooks/useCheckPermissions';
import { Permission } from '@tickeat/permissions';

export type CardButtonActionMode = 'CREATE' | 'UPDATE' | 'CLOSE';

interface ButtonActionProps {
  permissionScope: AppPath;
  onPress: (mode: CardButtonActionMode) => void;
}

const ButtonAction = ({ onPress, permissionScope }: ButtonActionProps) => {
  const checkPermissions = useCheckPermissions();

  const mode = useAppSelector(triptychSelectors.selectMode);
  const isOpen = useAppSelector(triptychSelectors.selectIsOpen);

  const title = isOpen ? 'Fermer' : mode === 'CREATE' ? 'Ajouter' : 'Editer';

  const hasUpdatePermission = checkPermissions([
    `dashboard::${permissionScope}.update` as Permission,
  ]);
  const hasCreatePermission = checkPermissions([
    `dashboard::${permissionScope}.create` as Permission,
  ]);

  if (!hasUpdatePermission && !hasCreatePermission) {
    return null;
  }

  if (!hasUpdatePermission && mode === 'UPDATE') {
    return null;
  }

  if (!hasCreatePermission && mode === 'CREATE') {
    return null;
  }

  return (
    <Button title={title} bordered={isOpen} onPress={() => onPress(isOpen ? 'CLOSE' : mode)} />
  );
};

type CardProps = {
  height: number;
};

const TopHeader = styled.View({
  marginTop: -theme.sizes.listHeaderHeight,
  height: theme.sizes.listHeaderHeight,
  justifyContent: 'center',
});

const TopHeaderLabel = styled.Text({
  fontFamily: 'Work Sans',
  width: '100%',
  textAlign: 'center',
  flexShrink: 0,
  flexGrow: 0,
  fontSize: normalize(18),
  color: '#97999B',
});

const Background = styled.Image({
  position: 'absolute',
  top: 0,
  right: 0,
  width: '100%',
  height: '100%',
  resizeMode: 'stretch',
});

const Content = styled.View({
  paddingHorizontal: normalize(34),
  paddingTop: normalize(55),
  paddingBottom: normalize(34),
});

const Header = styled.View({
  width: '100%',
});

const HeaderTitles = styled.View({
  ...col,
  maxWidth: '65%',
});

const Title = styled.Text<{ small?: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  letterSpacing: -0.1,
  fontWeight: props.small ? '500' : '700',
  fontSize: props.small ? normalize(21) : normalize(30),
  paddingBottom: props.small ? normalize(13) : normalize(0),
  paddingTop: props.small ? normalize(8) : normalize(0),
  color: theme.colors.babypowder,
}));

const Subtitle = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: theme.colors.babypowder,
  opacity: 0.5,
  // textTransform: 'uppercase',
}));

const HeaderValues = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-around',
});

const HeaderValuesItem = styled.View({
  justifyContent: 'flex-start',
  width: '33.33%',
});

const Key = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(19),
  color: theme.colors.celadonblue,
  fontWeight: '700',
  textAlign: 'center',
  marginTop: 34,
}));

const Value = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(18),
  color: theme.colors.babypowder,
  textAlign: 'center',
  paddingTop: 3,
  fontWeight: '500',
}));

const VisualContainer = styled.View({
  marginTop: normalize(55),
  height: normalize(180),
});

const Visual = styled.Image({
  width: '100%',
  height: '100%',
  resizeMode: 'contain',
});

const VisualEdit = styled.View({
  position: 'absolute',
  width: 48,
  height: 48,
  bottom: 0,
  right: 0,
});

const HelpTitle = styled.Text({
  fontSize: normalize(22),
  lineHeight: 24,
  fontFamily: 'Work Sans',
  fontWeight: '600',
  color: theme.colors.babypowder,
  marginBottom: 8,
  paddingHorizontal: 32,
});

const HelpSubtitle = styled.Text({
  fontSize: normalize(20),
  lineHeight: 22,
  fontFamily: 'Work Sans',
  fontWeight: '500',
  color: theme.colors.babypowder,
  marginTop: 16,
  marginBottom: 8,
  paddingHorizontal: 32,
});

const HelpText = styled.Text({
  fontSize: normalize(18),
  lineHeight: 20,
  fontFamily: 'Work Sans',
  color: theme.colors.babypowder,
  marginBottom: 16,
  paddingHorizontal: 32,
});

const HelpExample = styled.Text({
  fontSize: normalize(18),
  lineHeight: 20,
  fontFamily: 'Work Sans',
  fontWeight: '500',
  color: theme.colors.gold,
  marginBottom: 4,
  paddingHorizontal: 32,
});

const HelpScrollContainer = styled.View({
  marginHorizontal: -8,
  paddingBottom: 24,
  paddingTop: 8,
});

const LineBreak = styled.View<{ height?: number }>(({ props }) => ({
  height: props.height || 8,
}));

const Container = styled.View<CardProps>(({ props }) => ({
  height: props.height,
  shadowColor: '#000',
  shadowOffset: {
    width: 8,
    height: 0,
  },
  shadowOpacity: 0.21,
  shadowRadius: 8,
  elevation: 13,
}));

const HelpLink = ({ href, children }: { href: string; children: React.ReactNode }) => {
  return (
    <a
      style={{ textDecoration: 'underline', color: theme.colors.celadonblue }}
      href={href}
      target="_blank">
      {children}
    </a>
  );
};

export const Card = withComponents(
  styled.View(({ theme }) => ({
    width: theme.sizes.layoutCard,
    flexShrink: 0,
    flexGrow: 0,
    zIndex: 99,
  })),
  {
    ButtonAction,
    TopHeader,
    TopHeaderLabel,
    Container,
    Background,
    Content,
    Header,
    Title,
    HeaderTitles,
    Subtitle,
    HeaderValues,
    HeaderValuesItem,
    Key,
    Value,
    VisualContainer,
    Visual,
    VisualEdit,
    HelpTitle,
    HelpLink,
    HelpSubtitle,
    HelpText,
    HelpExample,
    HelpScrollContainer,
    LineBreak,
  },
);
